import type { MyAppsExternalApp } from '../api/myApps';
import { APP_REGISTRY_BY_ID } from './appRegistry';
import { toExternalSearchResult } from './myAppsLaunch';
import { isPlatformAnalyticsAppId } from './platformAnalytics';
import { HUB_REGISTRY, STANDALONE_APP_REGISTRY } from './registry/hubRegistry';
import { buildSearchEntries, type RegistrySearchEntry } from './registry/registrySelectors';

export type MyAppsEntryKind = 'hub' | 'tool' | 'standalone' | 'link';
export type MyAppsEntryCategory =
  | 'Utilities'
  | 'Productivity'
  | 'IT Tools'
  | 'Analytics'
  | 'Microsoft 365';

export interface MyAppsSearchResult {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: string;
  searchText: string;
  kind: MyAppsEntryKind;
  category: MyAppsEntryCategory;
  url?: string;
}

const TOOL_HUB_IDS = new Map(
  HUB_REGISTRY.flatMap((hub) => hub.tools.map((tool) => [tool.id, hub.id] as const))
);

export function resolveMyAppsCategory(entryId: string, kind: MyAppsEntryKind): MyAppsEntryCategory {
  const catalogId = kind === 'tool' ? TOOL_HUB_IDS.get(entryId) : entryId;
  const category = catalogId ? APP_REGISTRY_BY_ID.get(catalogId)?.category : undefined;
  return category ?? 'Utilities';
}

export function getPermittedSearchEntries(
  roles: readonly string[],
  externalApps: readonly MyAppsExternalApp[] = [],
  analyticsEnabled = true
): readonly MyAppsSearchResult[] {
  const builtIn = buildSearchEntries(HUB_REGISTRY, STANDALONE_APP_REGISTRY, roles)
    .filter((entry) => analyticsEnabled || !isPlatformAnalyticsAppId(entry.id))
    .map(toSearchResult);
  return [...builtIn, ...externalApps.map(toExternalSearchResult)];
}

function toSearchResult(entry: RegistrySearchEntry): MyAppsSearchResult {
  return {
    id: entry.id,
    title: entry.name,
    description: entry.description,
    path: entry.path,
    icon: entry.icon,
    searchText: entry.searchText,
    kind: entry.kind,
    category: resolveMyAppsCategory(entry.id, entry.kind)
  };
}

export function searchMyApps(
  entries: readonly MyAppsSearchResult[],
  query: string
): readonly MyAppsSearchResult[] {
  const tokens = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return entries;

  return entries
    .map((entry) => ({
      entry,
      score: scoreEntry(entry, tokens)
    }))
    .filter((candidate) => candidate.score > 0)
    .sort(
      (left, right) => right.score - left.score || left.entry.title.localeCompare(right.entry.title)
    )
    .map((candidate) => candidate.entry);
}

function scoreEntry(entry: MyAppsSearchResult, tokens: readonly string[]): number {
  if (!tokens.every((token) => entry.searchText.includes(token))) return 0;
  const normalizedTitle = entry.title.toLocaleLowerCase();
  let score = 1;
  let titleMatched = false;
  for (const token of tokens) {
    if (normalizedTitle === token) {
      score += 20;
      titleMatched = true;
    } else if (normalizedTitle.startsWith(token)) {
      score += 10;
      titleMatched = true;
    } else if (normalizedTitle.includes(token)) {
      score += 5;
      titleMatched = true;
    } else score += 1;
  }
  if (!titleMatched && entry.path.split('/').filter(Boolean).length > 2) score += 2;
  return score;
}
